import { useMemo } from 'react';
import { Box } from '@chakra-ui/react';
import { EmptyField } from 'components/elements/EmptyField';
import { ExternalLink } from 'components/elements/ExternalLink';
import { Label } from 'components/elements/Label';
import { Text } from 'components/elements/Text';
import { ModalHeader } from 'components/layout/ModalHeader';
import { ModalStatus } from 'components/layout/ModalStatus';
import MasterDocumentModal from 'components/modals/documentModals/MasterDocumentModal';
import DocumentRelatedCompanies from 'components/widgets/DocumentModals/DocumentRelatedCompanies';
import { InfoModal } from 'components/widgets/InfoModal';
import { useEnvironmentalResourcePermitV4 } from 'hooks/useDocuments';
import { useEnvironmentalResourcePermit } from 'hooks/useEnvironmentalResourcePermit';
import { useGetModalActions, useGetQueryParam } from 'hooks/useModal';
import { trackProjectMaterials } from 'utils/analytics';
import { DOCUMENT_TYPES } from 'utils/constants/documentTypes';
import { parseRelatedContactsIntoList } from 'utils/contacts';
import { formatProjectCost } from 'utils/currency';
import { formatDate } from 'utils/date';

export const EnvironmentalResourcePermit = ({
  enableFetchByDocumentId = false,
}: {
  enableFetchByDocumentId?: boolean;
}) => {
  let resolvedDocumentId: string | undefined;
  let result:
    | ReturnType<typeof useEnvironmentalResourcePermitV4>
    | ReturnType<typeof useEnvironmentalResourcePermit>;

  if (enableFetchByDocumentId) {
    const documentId = useGetQueryParam('documentId');
    resolvedDocumentId = documentId || undefined;
    result = useEnvironmentalResourcePermitV4(documentId || undefined);
  } else {
    const documentUuid = useGetQueryParam('environmentalResourcePermit');
    resolvedDocumentId = documentUuid || undefined;
    result = useEnvironmentalResourcePermit(documentUuid);
  }

  const { Skeleton, ...modalProps } = useGetModalActions(result.isLoading);

  const relatedContacts = useMemo(
    () => parseRelatedContactsIntoList(result.data?.metadata?.relatedcontacts || []),
    [result.data?.metadata],
  );

  const permitUrl = result.data?.document_url;

  const workTypeContent = result.data?.metadata?.workclass ? (
    <Text textTransform="capitalize">{result.data.metadata.workclass}</Text>
  ) : (
    <EmptyField />
  );

  return (
    <InfoModal
      {...modalProps}
      header={
        <ModalHeader
          Skeleton={Skeleton}
          type={DOCUMENT_TYPES.ENVIRONMENTAL_RESOURCE_PERMIT}
        >
          {result.data?.metadata?.permitnum}
        </ModalHeader>
      }
    >
      <Box>
        <MasterDocumentModal
          address={result.data?.metadata?.parcel_address ?? ''}
          city={result.data?.city ?? ''}
          state_code={result.data?.state_code ?? ''}
          isLoading={result.isLoading}
        />
        {/* Companies */}
        <DocumentRelatedCompanies
          contacts={relatedContacts}
          companies={result.data?.companies}
          applicant={result.data?.metadata?.applicant}
          documentId={resolvedDocumentId}
          prefetchedAiContactSuggestionsByRelatedContactId={
            result.data?.ai_related_contact_suggestions_by_contact_id
          }
          Skeleton={Skeleton}
        />

        {/* Project details */}
        <InfoModal.Row>
          <Label>Project Name</Label>
          <InfoModal.RowDetails>
            <Skeleton variant="text" noOfLines={1}>
              {result.data?.metadata?.projectname ? (
                <Text fontWeight="bold">{result.data.metadata.projectname}</Text>
              ) : (
                <EmptyField />
              )}
            </Skeleton>
          </InfoModal.RowDetails>
        </InfoModal.Row>

        <InfoModal.Row hideDivider>
          <Label>Description</Label>
          <InfoModal.RowDetails>
            <Skeleton variant="text" noOfLines={1}>
              {result.data?.metadata?.description ? (
                <Text>{result.data.metadata.description}</Text>
              ) : (
                <EmptyField />
              )}
            </Skeleton>
          </InfoModal.RowDetails>
        </InfoModal.Row>

        <InfoModal.Row hideDivider>
          <Label>Permit Type</Label>
          <InfoModal.RowDetails>
            <Skeleton variant="text" noOfLines={1}>
              {result.data?.metadata?.permittype ? (
                <Text>{result.data.metadata.permittype}</Text>
              ) : (
                <EmptyField />
              )}
            </Skeleton>
          </InfoModal.RowDetails>
        </InfoModal.Row>

        <InfoModal.Row hideDivider>
          <Label>Category</Label>
          <InfoModal.RowDetails>
            <Skeleton variant="text" noOfLines={1}>
              {result.data?.metadata?.category ? (
                <Text>{result.data.metadata.category}</Text>
              ) : (
                <EmptyField />
              )}
            </Skeleton>
          </InfoModal.RowDetails>
        </InfoModal.Row>

        <InfoModal.Row hideDivider>
          <Label>Work Type</Label>
          <InfoModal.RowDetails>
            <Skeleton variant="text" noOfLines={1}>
              {workTypeContent}
            </Skeleton>
          </InfoModal.RowDetails>
        </InfoModal.Row>

        <InfoModal.Row hideDivider>
          <Label>Project Value</Label>
          <InfoModal.RowDetails>
            <Skeleton variant="text" noOfLines={1}>
              {result.data?.metadata?.estprojectcost ? (
                <Text>{formatProjectCost(result.data?.metadata?.estprojectcost)}</Text>
              ) : (
                <EmptyField />
              )}
            </Skeleton>
          </InfoModal.RowDetails>
        </InfoModal.Row>

        <InfoModal.Row hideDivider>
          <Label>Expiration Date</Label>
          <InfoModal.RowDetails>
            <Skeleton variant="text" noOfLines={1}>
              {result.data?.metadata?.expirationdate ? (
                <Text>
                  {formatDate(
                    result.data.metadata.expirationdate.replace(' ', 'T'),
                    'MMM dd, yyyy',
                  )}
                </Text>
              ) : (
                <EmptyField />
              )}
            </Skeleton>
          </InfoModal.RowDetails>
        </InfoModal.Row>

        <InfoModal.Row hideDivider>
          <Label>Area (Acres)</Label>
          <InfoModal.RowDetails>
            <Skeleton variant="text" noOfLines={1}>
              {result.data?.metadata?.acreage != null ? (
                <Text>{result.data.metadata.acreage}</Text>
              ) : (
                <EmptyField />
              )}
            </Skeleton>
          </InfoModal.RowDetails>
        </InfoModal.Row>

        <InfoModal.Row hideDivider>
          <Label>Affected Water Body</Label>
          <InfoModal.RowDetails>
            <Skeleton variant="text" noOfLines={1}>
              {result.data?.metadata?.waterbody ? (
                <Text>{result.data.metadata.waterbody}</Text>
              ) : (
                <EmptyField />
              )}
            </Skeleton>
          </InfoModal.RowDetails>
        </InfoModal.Row>

        {/* Status */}
        <ModalStatus activities={result.data?.activities} Skeleton={Skeleton} />

        {/* Links */}
        {result.isLoading && (
          <InfoModal.Row>
            <Label>Relevant Materials</Label>
            <InfoModal.RowDetails>
              <Skeleton variant="text" noOfLines={1} />
            </InfoModal.RowDetails>
          </InfoModal.Row>
        )}

        {!result.isLoading && (
          <InfoModal.Row>
            <Label>Relevant Materials</Label>
            <InfoModal.RowDetails>
              {permitUrl ? (
                <ExternalLink
                  to={permitUrl}
                  onClick={() => trackProjectMaterials(permitUrl)}
                >
                  View application
                </ExternalLink>
              ) : (
                <EmptyField />
              )}
            </InfoModal.RowDetails>
          </InfoModal.Row>
        )}
      </Box>
    </InfoModal>
  );
};
